import { z } from "zod";
import {
  AREA_MAX,
  CHANGE_CATEGORIES,
  COMMIT_SHA_RE,
  NOTE_MAX,
  PUBLIC_SCHEMA_VERSION,
  TITLE_MAX,
} from "./constants";

export const availabilitySchema = z.enum(["available", "partial", "unavailable"]);
export type Availability = z.infer<typeof availabilitySchema>;

export const measuredSchema = <T extends z.ZodTypeAny>(value: T) =>
  z.object({
    value: value.nullable(),
    availability: availabilitySchema,
    reason: z.string().nullable(),
  });

export type Measured<T> = {
  value: T | null;
  availability: Availability;
  reason: string | null;
};

export const actorKindSchema = z.enum(["primary", "child", "unknown"]);
export type ActorKind = z.infer<typeof actorKindSchema>;

export const sourceStateSchema = z.enum([
  "present",
  "retained-only",
  "changing",
  "conflict",
]);
export type SourceState = z.infer<typeof sourceStateSchema>;

export const reviewAssessmentSchema = z.enum(["accepted", "needs-follow-up"]);
export type ReviewAssessment = z.infer<typeof reviewAssessmentSchema>;

export const reviewStateSchema = z.enum([
  "accepted",
  "needs-follow-up",
  "unreviewed",
  "stale",
]);
export type ReviewState = z.infer<typeof reviewStateSchema>;

export const changeCategorySchema = z.enum(CHANGE_CATEGORIES);
export type ChangeCategory = z.infer<typeof changeCategorySchema>;

export const sessionSummarySchema = z.object({
  schemaVersion: z.literal(PUBLIC_SCHEMA_VERSION),
  id: z.string(),
  sourceSessionId: z.string(),
  sourceKind: z.literal("codex-rollout"),
  parserVersion: z.string(),
  sourceCliVersion: z.string().nullable(),
  sourceHash: z.string(),
  sourceState: sourceStateSchema,
  actorKind: actorKindSchema,
  parentSessionId: z.string().nullable(),
  recordedModels: z.array(z.string()),
  modelBucket: z.string(),
  reasoningSettings: z.array(z.string()),
  firstActivityAt: z.string().nullable(),
  lastActivityAt: z.string().nullable(),
  recordedCommit: z.string().nullable(),
  recordedSpanMs: measuredSchema(z.number()),
  tokens: z.object({
    input: measuredSchema(z.number()),
    cachedInput: measuredSchema(z.number()),
    output: measuredSchema(z.number()),
    reasoningOutput: measuredSchema(z.number()),
    total: measuredSchema(z.number()),
  }),
  toolCalls: measuredSchema(z.number()),
  shellCalls: measuredSchema(z.number()),
  knownShellExits: measuredSchema(z.number()),
  nonzeroShellExits: measuredSchema(z.number()),
  explicitToolErrors: measuredSchema(z.number()),
  malformedRows: z.number(),
  warnings: z.array(z.string()),
  importedAt: z.string(),
  synthetic: z.boolean(),
});
export type SessionSummary = z.infer<typeof sessionSummarySchema>;

export const checkSummarySchema = z.object({
  zeroExit: z.number(),
  nonzeroExit: z.number(),
  unavailable: z.number(),
});
export type CheckSummary = z.infer<typeof checkSummarySchema>;

export const sessionListItemSchema = sessionSummarySchema.extend({
  reviewState: reviewStateSchema,
  checkSummary: checkSummarySchema,
});
export type SessionListItem = z.infer<typeof sessionListItemSchema>;

export const commandObservationSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  callId: z.string(),
  toolName: z.string(),
  shellExecutable: z.string().nullable(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  exitCode: z.number().nullable(),
  explicitError: z.boolean().nullable(),
  sourceLineStart: z.number(),
  sourceLineEnd: z.number().nullable(),
  evidenceDigest: z.string(),
});
export type CommandObservation = z.infer<typeof commandObservationSchema>;

export const changeMarkerSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(TITLE_MAX),
  occurredAt: z.string(),
  category: changeCategorySchema,
  area: z.string().nullable(),
  commitSha: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ChangeMarker = z.infer<typeof changeMarkerSchema>;

export const markerWriteSchema = z.object({
  title: z.string().trim().min(1).max(TITLE_MAX),
  occurredAt: z.string(),
  category: changeCategorySchema,
  area: z.string().trim().min(1).max(AREA_MAX).nullable().optional(),
  commitSha: z
    .string()
    .regex(COMMIT_SHA_RE, "commitSha must be a full 40 or 64 character hex digest")
    .nullable()
    .optional(),
  note: z.string().max(NOTE_MAX).nullable().optional(),
});
export type MarkerWrite = z.infer<typeof markerWriteSchema>;

export const humanReviewSchema = z.object({
  sessionId: z.string(),
  assessment: reviewAssessmentSchema,
  note: z.string().nullable(),
  reviewedAt: z.string(),
  reviewedSourceHash: z.string(),
});
export type HumanReview = z.infer<typeof humanReviewSchema>;

export const reviewWriteSchema = z.object({
  assessment: reviewAssessmentSchema,
  note: z.string().max(NOTE_MAX).nullable().optional(),
});

export const checkLabelSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  commandId: z.string(),
  label: z.string().min(1).max(TITLE_MAX),
  commandEvidenceDigest: z.string(),
  createdAt: z.string(),
});
export type CheckLabel = z.infer<typeof checkLabelSchema>;

export const checkLabelWriteSchema = z.object({
  commandId: z.string().min(1).max(128),
  label: z.string().trim().min(1).max(TITLE_MAX),
});

export const importPostSchema = z.object({
  force: z.boolean(),
});

export const sessionsResponseSchema = z.object({
  schemaVersion: z.literal(PUBLIC_SCHEMA_VERSION),
  sessions: z.array(sessionListItemSchema),
});

export type ImportStatus = "idle" | "running" | "completed" | "failed";

export type OversizeNativeSource = {
  /** Stable, opaque reference for support without exposing a local path. */
  reference: string;
  observedBytes: number;
  maximumBytes: number;
  repository: "current" | "unknown";
  evidence: "retained";
  recovery: "not_supported";
  budget?: "retained_metadata";
};

export type ImportState = {
  status: ImportStatus;
  force: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  processedFiles: number;
  importedSessions: number;
  skippedFiles: number;
  /** Files whose accepted bytes have not changed since the previous import. */
  unchangedFiles?: number;
  /** Oversize sources are kept on disk and are never force-imported. */
  oversizeSources?: OversizeNativeSource[];
  /** Incomplete attributed activity; normal unfinished tails are not failures. */
  coverageGaps?: {reference:string; reason:"metadata_budget"|"malformed"|"source_changed"|"unreadable"|"missing_source"}[];
  acknowledgedGaps?: {reference:string;reason:string;resolutionId:string;acknowledgedAt:string;message:string}[];
  errors: string[];
  message: string | null;
};

export type BootstrapResponse = {
  collectionHealth?: import('./collection-health').CollectionHealth;
  schemaVersion: typeof PUBLIC_SCHEMA_VERSION;
  appName: string;
  appVersion: string;
  repoLabel: string;
  projectName?: string;
  worktreeRoots?: string[];
  repoKey: string;
  synthetic: boolean;
  readOnlyPreview?: boolean;
  csrfToken: string;
  lastImport: ImportState | null;
  capabilities: {
    nativeRollout: true;
    execJson: false;
    rebuildCache: true;
  };
  parserVersion: string;
  timezone: string;
  dataDir: string;
  sessionsDir: string;
};

export function available<T>(value: T): Measured<T> {
  return { value, availability: "available", reason: null };
}

export function unavailable<T>(reason: string): Measured<T> {
  return { value: null, availability: "unavailable", reason };
}

export function partial<T>(value: T | null, reason: string): Measured<T> {
  return { value, availability: "partial", reason };
}

export function isComparable<T>(metric: Measured<T>): metric is Measured<T> & { value: T } {
  return metric.availability === "available" && metric.value !== null;
}
